import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../context/toastContext";
import { ArrowTopRightOnSquareIcon, StopIcon } from "@heroicons/react/24/solid";
import { baseAxios } from "../../apis";
import SkeletonCard from "../skeletons/SkeletonCard";
import { extractErrorMessage } from "../../utils";
import { useSidebar } from "../../context/sidebarContext";

// A custom hook for fetching the Telegram status and link.
const useTelegramDataQuery = () => {
  return useQuery({
    queryKey: ["telegramData"],
    queryFn: () =>
      baseAxios
        .get("/api/telegram", { withCredentials: true })
        .then((res) => res.data),
    // enabled: false, // Disable the query for now
  });
};

// A mutation hook for the "stop" action.
const useStopTelegramMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () =>
      baseAxios.get("/api/telegram/command/stop/", { withCredentials: true }),
    onSuccess: () => {
      addToast("success", "Telegram bot has been disconnected.");
      queryClient.invalidateQueries({ queryKey: ["telegramData"] });
    },
    onError: (err) => {
      const errorMessage = extractErrorMessage(err?.response?.data || err);
      addToast("error", errorMessage);
    },
  });
};

// export default function TelegramTab() {
//   const { addToast } = useToast();
//   const { isTablet } = useSidebar();

//   const { data, isLoading } = useTelegramDataQuery();
//   const stopTelegramMutation = useStopTelegramMutation();

//   const connected = data?.connected || false;
//   const telegramLink = data?.activation_link || "";

//   const handleStop = () => {
//     stopTelegramMutation.mutate();
//   };

//   const handleOpenTelegramLink = () => {
//     if (telegramLink) {
//       window.open(telegramLink, "_blank", "noopener,noreferrer");
//     } else {
//       addToast("error", "Telegram link is not available.");
//     }
//   };

//   return (
//     <div className="flex flex-col items-center justify-center min-h-[400px] rounded-2xl border border-gray-200 bg-white px-5 py-8 shadow-sm">
//       <div className="text-center max-w-md">
//         <img
//           src={"/images/icons/icon_no_bot_activated.svg"}
//           alt=""
//           className="mx-auto w-[140px] h-[140px] lg:w-[200px] lg:h-[200px]"
//         />
//         <h3 className="mb-2 text-2xl font-semibold text-gray-900">
//           Coming Soon
//         </h3>
//         <p className="text-gray-600">
//           Telegram integration is currently under development. Stay tuned for
//           updates!
//         </p>
//       </div>
//     </div>
//   );
// }

export default function TelegramTab() {
  const { addToast } = useToast();
  const { isTablet } = useSidebar();

  // Call our new hooks to get data and actions.
  const { data, isLoading } = useTelegramDataQuery();
  const stopTelegramMutation = useStopTelegramMutation();

  // Derive variables from the query's data.
  const connected = data?.connected || false;
  const telegramLink = data?.activation_link || "";

  const handleStop = () => {
    stopTelegramMutation.mutate();
  };

  const handleOpenTelegramLink = () => {
    if (telegramLink) {
      window.open(telegramLink, "_blank", "noopener,noreferrer");
    } else {
      addToast("error", "Telegram link is not available.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-5">
      {/* Telegram Link Card */}
      <div className="flex flex-col justify-center rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm w-full md:w-3/4">
        <p className="m-0 !py-0 text-sm font-medium text-gray-500">
          Telegram Activation Link
        </p>
        <div className="flex w-full items-center justify-between gap-4">
          {isLoading ? (
            <SkeletonCard
              type="text"
              lines={2}
              widths={["0", "300px"]}
              heights={["0", "20px"]}
            />
          ) : (
            <span
              className="flex items-center gap-2 text-lg font-semibold text-gray-900 cursor-pointer mt-1"
              onClick={handleOpenTelegramLink}
            >
              {telegramLink.length > 25
                ? isTablet
                  ? `${telegramLink.substring(0, 20)}...`
                  : telegramLink
                : telegramLink || "N/A"}
              <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-500 hover:text-gray-800" />
            </span>
          )}
        </div>
      </div>

      {/* Status and Action Card */}
      <div className="flex flex-col justify-center rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm w-full lg:w-1/4">
        <p className="m-0 !py-0 text-sm font-medium text-gray-500">Status</p>
        <div className="flex w-full items-center justify-between gap-3">
          {isLoading ? (
            <SkeletonCard
              type="text"
              lines={2}
              widths={["0", "100px"]}
              heights={["0", "20px"]}
            />
          ) : connected ? (
            // -- CONNECTED STATE --
            <>
              <span className="flex items-center gap-2 text-lg font-semibold text-green-700">
                <div className="h-2.5 w-2.5 rounded-full bg-green-700 mt-1" />
                Connected
              </span>
              <button
                onClick={handleStop}
                disabled={stopTelegramMutation.isPending}
                className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <StopIcon className="h-4 w-4" />
                {stopTelegramMutation.isPending ? "Stopping..." : "Stop"}
              </button>
            </>
          ) : (
            // -- NOT CONNECTED STATE --
            <>
              <span className="flex items-center w-full gap-2 text-lg font-semibold text-red-700">
                <div className="h-2.5 w-2.5 rounded-full bg-red-700 mt-1" />
                Not Connected
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
